import { isNoConfiguredProviderResponse, NoConfiguredProviderError } from "@/lib/edgeFnError";
import type { GenerateRoteiroInput, GeneratedScript, RoteiroType, ObjetivoRoteiro } from "../types";
import { routePromptId } from "./promptRouter";

const TIPO_CONTEXT: Record<RoteiroType, string> = {
  Reels: "Reels (vídeo curto de 30–90s, ritmo rápido, hook nos primeiros 3s, cortes dinâmicos)",
  Stories:
    "Stories (primeiro escreva a mensagem completa de forma natural; depois estime o total de palavras e distribua em stories coesos usando cerca de 70 palavras por story como referência prática, equivalente à fala média em 150 palavras por minuto; se o usuário pedir explicitamente 2, 3, 4 ou mais stories, respeite a quantidade pedida e expanda o conteúdo até que cada story tenha substância real, idealmente na faixa de 65 a 75 palavras e com média próxima de 70, sem uma frase por story e sem cortar o raciocínio no meio)",
  Anúncio: "Anúncio patrocinado (CTA claro e urgente, linguagem persuasiva, foco em conversão)",
};

const OBJETIVO_CONTEXT: Record<ObjetivoRoteiro, string> = {
  educativo: "Educar e informar o paciente — compartilhar conhecimento que gere valor e confiança",
  engajamento: "Gerar engajamento orgânico — curtidas, comentários, compartilhamentos e salvamentos",
  conversão: "Converter espectadores em pacientes — incentivar agendamento de consulta",
  autoridade: "Construir autoridade médica — posicionar o médico como referência no tema",
};

export interface GenerateRoteiroResult {
  jobId: string;
  result: GeneratedScript;
}

/**
 * Trigger AI roteiro generation. Sends structured `template_vars` so the edge
 * function renders the user message from the active `roteiros.creation.system`
 * prompt template (see `src/lib/prompts/roteiroCreation.ts`). No inline
 * prompt strings live on the client.
 */
export async function generateRoteiro(
  input: GenerateRoteiroInput,
  token: string,
): Promise<GenerateRoteiroResult> {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
  const url = `${supabaseUrl}/functions/v1/llm-proxy/chat`;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      task: "roteiros",
      prompt_config_id: routePromptId(input.type),
      template_vars: {
        tema: input.topic,
        tipo: TIPO_CONTEXT[input.type],
        objetivo: OBJETIVO_CONTEXT[input.objetivo],
        tipo_slug: input.type,
        objetivo_slug: input.objetivo,
        generation_kind: input.generationKind ?? "base",
        parent_job_id: input.parentJobId ?? "",
        root_job_id: input.rootJobId ?? "",
        detalhes: input.details.trim()
          ? `Informações adicionais: ${input.details.trim()}`
          : "",
        campo_semantico: input.campoSemantico ?? "",
      },
    }),
  });

  if (!response.ok) {
    let detail = "";
    let parsed: { error?: string; detail?: string } | null = null;
    try {
      parsed = (await response.json()) as { error?: string; detail?: string };
      detail = parsed.detail ?? parsed.error ?? response.statusText;
      if (parsed.error === "rate_limit_exceeded") {
        throw new Error("Limite de 30 gerações por hora atingido. Tente novamente mais tarde.");
      }
    } catch (parseErr) {
      if (parseErr instanceof Error && parseErr.message.includes("Limite")) throw parseErr;
      detail = response.statusText;
    }
    if (isNoConfiguredProviderResponse(response.status, parsed)) {
      throw new NoConfiguredProviderError(detail || "no_configured_provider", {
        status: response.status,
        code: parsed?.error,
      });
    }
    throw new Error(`Erro ao gerar roteiro: ${detail}`);
  }

  const data = (await response.json()) as {
    job_id: string;
    status: string;
    result: { script: string };
    provider: string;
    model_id: string;
  };

  return {
    jobId: data.job_id,
    result: {
      jobId: data.job_id,
      script: data.result.script,
      provider: data.provider,
      modelId: data.model_id,
      createdAt: new Date().toISOString(),
    },
  };
}
